/**
 * ComplianceStatus Component
 *
 * Displays a compliance status indicator (yes/warning/no)
 */

import React from 'react';
import {
  Cancel as NoIcon,
  CheckCircle as YesIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { COMPLIANCE_BG_COLORS, COMPLIANCE_COLORS } from '../../constants';
import type { ComplianceStatusType } from '../../types';

interface ComplianceStatusProps {
  /** The compliance status */
  status: ComplianceStatusType;
  /** Whether to show the label */
  showLabel?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

const STATUS_ICONS: Record<ComplianceStatusType, React.ReactNode> = {
  yes: <YesIcon fontSize="inherit" />,
  warning: <WarningIcon fontSize="inherit" />,
  no: <NoIcon fontSize="inherit" />,
};

const STATUS_LABELS: Record<ComplianceStatusType, string> = {
  yes: 'Compliant',
  warning: 'Warning',
  no: 'Non-compliant',
};

export const ComplianceStatus: React.FC<ComplianceStatusProps> = ({
  status,
  showLabel = false,
  size = 'small',
}) => {
  const color = COMPLIANCE_COLORS[status];
  const bgColor = COMPLIANCE_BG_COLORS[status];
  const iconSize = size === 'small' ? '1rem' : '1.25rem';

  if (!showLabel) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          fontSize: iconSize,
        }}
        title={STATUS_LABELS[status]}
      >
        {STATUS_ICONS[status]}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        color,
        bgcolor: bgColor,
        borderRadius: 1,
        padding: size === 'small' ? '2px 6px' : '4px 8px',
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        fontWeight: 500,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', fontSize: iconSize }}>
        {STATUS_ICONS[status]}
      </Box>
      <Typography variant="inherit" component="span">
        {STATUS_LABELS[status]}
      </Typography>
    </Box>
  );
};

export default ComplianceStatus;

